import { clearSavedUserFilter } from '@/clientOnlyUtils/clientUtils';
import { cn } from '@/clientOnlyUtils/cn';
import { TRPCClientError } from '@trpc/client';
import { useRouter } from 'next/router';
import React, { type ComponentProps } from 'react';
import toast from 'react-hot-toast';

import type { SvrListUserTeam } from '@poseidon/types/svr/svrTeam.type';

import { TeamAvatar } from '@/components/Avatar';
import { Badge } from '@poseidon/components/Badge';
import { TableCellHeader } from '@/components/Table';
import { WaveSpinner } from '@/components/WaveSpinner';

import { useMeUpdateMutation } from '@/hooks/trpc/useUserMutation';

import { Cell, CellLoading, Table } from '~/components/Table/Table';
import UserIcons from '~/components/UserIcons';

interface TeamsTableProps {
  selectedTeamId?: string;
  teams: SvrListUserTeam[];
  isLoading?: boolean;
}
export const TeamsTable = ({ teams, selectedTeamId, isLoading }: TeamsTableProps) => {
  const [isUpdatingId, setIsUpdatingId] = React.useState<string>();
  const meUpdateMutation = useMeUpdateMutation();
  const router = useRouter();

  const onClick = async (defaultTeamId: string, teamName: string) => {
    const toastId = toast.loading(`Selecting team`);
    try {
      setIsUpdatingId(defaultTeamId);
      await meUpdateMutation.mutateAsync({ data: { defaultTeamId } });
      clearSavedUserFilter();

      toast.success(`Selected ${teamName}`, { id: toastId });

      localStorage.removeItem('form');
      void router.push(`/teams/active`);
    } catch (e: unknown) {
      if (e instanceof TRPCClientError) {
        toast.error(e.message, { id: toastId });
      } else {
        toast.error('Could not select team', { id: toastId });
      }
    } finally {
      setIsUpdatingId(undefined);
    }
  };

  return (
    <Table gridTemplateColumns="1fr min-content min-content" bordered>
      <TableCellHeader>Name</TableCellHeader>
      <TableCellHeader />
      <TableCellHeader className="justify-end">Users</TableCellHeader>

      {isLoading && <CellLoading index={1}>Loading Teams</CellLoading>}
      {!isLoading &&
        teams?.map((team, index) => (
          <TeamRow
            key={team.teamId}
            active={team.teamId == selectedTeamId}
            team={team}
            index={index}
            isUpdatingId={isUpdatingId}
            onClick={() => onClick(team.teamId, team.teamName)}
          />
        ))}
    </Table>
  );
};

type TeamRowProps = ComponentProps<'div'> & {
  active?: boolean;
  team: SvrListUserTeam;
  index: number;
  isUpdatingId?: string;
};
const TeamRow = ({ team, active, index, isUpdatingId, ...extraProps }: TeamRowProps) => {
  const props = {
    active,
    index,
    ...extraProps,
  };

  const isUpdating = isUpdatingId === team?.teamId;
  const isPremium = team.teamPriceTier === 'premium';
  return (
    <>
      <Cell {...props}>
        {isUpdating && (
          <div className={cn('m-0 size-8 rounded-full text-haiti-600 dark:bg-slate-900 dark:text-zumthor-600')}>
            <WaveSpinner className="size-full" />
          </div>
        )}

        {!isUpdating && team != null && (
          <TeamAvatar logo={team.teamLogo} name={team.teamName} className="size-8" shape="circle" />
        )}

        <div className="w-1" />
        {team?.teamName}
        {active && <Badge type="standard">Active</Badge>}
      </Cell>
      <Cell {...props} right>
        {isPremium && <Badge dot>Premium</Badge>}
        {team.userTeamStatus === 'admin' && <Badge dot>Admin</Badge>}
        {team.userTeamStatus === 'removed' && <Badge type={'dangerous'}>Removed</Badge>}
      </Cell>
      <Cell {...props} right>
        <UserIcons listTeam={team} />
      </Cell>
    </>
  );
};
